<template>
  <wui-modal
    :size="40"
    :closable="false"
    class="flex h-160! max-h-[calc(100vh-32px)]! w-[min(32.5rem,calc(100vw-32px))]! flex-col overflow-hidden! p-0! [--modal-gap:0px] [--modal-pad:0px] [--modal-radius:16px] [--modal-title-pad-y:0px] [&_.wui-modal__header]:w-full [&_.wui-modal__content]:min-h-0 [&_.wui-modal__content]:flex-1 [&_.wui-modal__content]:overflow-hidden"
    @close="closeDialog"
  >
    <template #header>
      <div class="flex w-full items-center justify-between gap-2 px-4 pt-4">
        <h2 class="min-w-0 text-heading-sb-18-24 text-fg-base-fg3">
          {{ modalTitle }}
        </h2>
        <wui-btn
          icon
          variant="neut"
          state="alpha"
          :size="32"
          prepend-icon="closeRegularMdM"
          data-test="share-contact-close"
          @click="closeDialog"
        />
      </div>
    </template>

    <div class="flex h-full min-h-0 flex-col">
      <div class="shrink-0 px-4 pt-1 pb-2">
        <wui-input
          v-model="search"
          :placeholder="$t('common.search')"
          with-clear
          prepend-icon="searchM"
          data-test="share-contact-search"
        />
      </div>

      <div
        v-if="isInviteShareMode"
        class="shrink-0 px-4 pt-3 pb-1.5"
      >
        <wui-tabs
          :model-value="selectedShareTab"
          class="min-w-0 overflow-hidden"
          @update:model-value="onShareTabChange"
        >
          <wui-tabs-item
            :value="ShareTab.PERSONAL"
            v-bind="personalSelectedCount ? { counter: personalSelectedCount } : {}"
          >
            {{ $t('common.roomItemFilterPersonal') }}
          </wui-tabs-item>
          <wui-tabs-item
            :value="ShareTab.GROUPS"
            v-bind="groupSelectedCount ? { counter: groupSelectedCount } : {}"
          >
            {{ $t('common.roomItemFilterGroups') }}
          </wui-tabs-item>
        </wui-tabs>
      </div>

      <div
        v-else-if="folders.length"
        class="shrink-0 px-4 pt-3 pb-1.5"
      >
        <wui-tabs
          :model-value="selectedFolderId"
          scrollable
          class="min-w-0 overflow-hidden"
          @update:model-value="onFolderChange"
        >
          <wui-tabs-item :value="ALL_CHATS_FOLDER_ID">
            {{ $t('common.all') }}
          </wui-tabs-item>
          <wui-tabs-item
            v-for="folder in folders"
            :key="folder.id"
            :value="folder.id"
            :title="folder.name"
          >
            {{ formatFolderTabName(folder.name) }}
          </wui-tabs-item>
        </wui-tabs>
      </div>

      <div
        :key="listScrollKey"
        class="relative min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto pl-5 pr-6 pt-1 pb-1"
        data-test="share-contact-virtual-scroll"
        @scroll="onListScroll"
      >
        <template v-if="pinnedRooms.length || unpinnedRooms.length">
          <virtual-scroller
            v-if="pinnedRooms.length"
            v-slot="{ item }"
            page-mode
            :items="pinnedRooms"
            :item-size="SHARE_LIST_ITEM_HEIGHT"
            key-field="pServedUser"
          >
            <div class="box-border min-w-0 pb-0.5">
              <button
                type="button"
                class="flex h-[52px] w-full min-w-0 items-center gap-3 rounded-8 text-left"
                :data-test="`share-contact-room-${item.pServedUser}`"
                @click="toggleRoom(item.pServedUser)"
              >
                <contact-avatar
                  :room="item"
                  :contact="resolveRoomContact(item)"
                  :name="item.name"
                  :is-room="!!item.isGroup"
                  :is-own="isOwnRoom(item)"
                  :size="40"
                  class="shrink-0"
                />
                <span
                  class="min-w-0 grow truncate text-body-m-15-22 text-fg-base-fg3"
                  :title="item.name"
                >
                  {{ item.name }}
                </span>
                <wui-checkbox
                  :model-value="selectedPServeds.includes(item.pServedUser)"
                  class="pointer-events-none shrink-0"
                  small
                  readonly
                />
              </button>
            </div>
          </virtual-scroller>

          <hr
            v-if="pinnedRooms.length && unpinnedRooms.length"
            class="mt-[3px] mb-1 border-t-bor-divider"
            data-test="share-contact-pinned-divider"
          >

          <virtual-scroller
            v-if="unpinnedRooms.length"
            v-slot="{ item }"
            page-mode
            :items="unpinnedRooms"
            :item-size="SHARE_LIST_ITEM_HEIGHT"
            key-field="pServedUser"
            @scroll-end="onListEndReached"
          >
            <div class="box-border min-w-0 pb-0.5">
              <button
                type="button"
                class="flex h-[52px] w-full min-w-0 items-center gap-3 rounded-8 text-left"
                :data-test="`share-contact-room-${item.pServedUser}`"
                @click="toggleRoom(item.pServedUser)"
              >
                <contact-avatar
                  :room="item"
                  :contact="resolveRoomContact(item)"
                  :name="item.name"
                  :is-room="!!item.isGroup"
                  :is-own="isOwnRoom(item)"
                  :size="40"
                  class="shrink-0"
                />
                <span
                  class="min-w-0 grow truncate text-body-m-15-22 text-fg-base-fg3"
                  :title="item.name"
                >
                  {{ item.name }}
                </span>
                <wui-checkbox
                  :model-value="selectedPServeds.includes(item.pServedUser)"
                  class="pointer-events-none shrink-0"
                  small
                  readonly
                />
              </button>
            </div>
          </virtual-scroller>
        </template>

        <p
          v-else
          class="px-3 py-2 text-fg-base-fg10"
        >
          {{ $t('chat.noChatsFound') }}
        </p>
      </div>

      <div class="relative shrink-0 border-t border-t-bor-nav">
        <div
          v-if="isInviteShareMode && selectedChipItems.length"
          class="px-4 py-3"
          data-test="share-contact-selected-chips"
        >
          <div
            class="flex max-h-[calc(2.7*32px+1.7*6px)] flex-wrap content-start gap-1.5 overflow-x-hidden overflow-y-auto"
          >
            <wui-chip
              v-for="item in selectedChipItems"
              :key="item.pServedUser"
              :size="32"
              variant="neut"
              state="tonal"
              removable
              class="max-w-[240px] shrink-0"
              :label="item.name"
              :title="item.name"
              @remove="toggleRoom(item.pServedUser)"
            >
              <template #prepend>
                <contact-avatar
                  :room="item.room"
                  :contact="item.contact"
                  :name="item.name"
                  :is-room="item.isGroup"
                  :is-own="item.isOwn"
                  :size="24"
                  class="shrink-0"
                />
              </template>
            </wui-chip>
          </div>
        </div>

        <div class="relative px-4">
          <emoji-picker
            v-if="showEmojiList"
            class="absolute bottom-full left-4 z-20 mb-1"
            close-on-click-outside
            data-test="share-contact-emoji-picker"
            @append="appendEmoji"
            @escape="closeEmojiList"
            @close="closeEmojiList"
          />

          <div class="flex items-end gap-2 pt-2 pb-4">
            <div class="flex min-w-0 flex-1 items-end gap-1.5 rounded-10 bg-surf-neut-soft-def p-2">
              <textarea
                ref="commentInputRef"
                v-model="messageText"
                rows="1"
                class="max-h-24 min-h-6 min-w-0 flex-1 resize-none bg-transparent text-body-r-16-24 text-fg-base-fg3 outline-none placeholder:text-fg-base-fg17"
                :placeholder="$t('mainAside.shareContactToChatMessagePlaceholder')"
                data-test="share-contact-comment"
                @keydown.enter.exact.prevent="send"
              />
              <button
                type="button"
                class="flex h-6 w-6 shrink-0 items-center justify-center"
                data-test="share-contact-emoji"
                @mousedown.prevent
                @click="toggleEmojiList"
              >
                <wui-icon
                  name="faceM"
                  class="h-6! w-6! text-fg-base-fg22!"
                />
                <span class="sr-only">{{ $t('chat.openEmoji') }}</span>
              </button>
            </div>

            <wui-btn
              class="shrink-0"
              icon
              :size="40"
              variant="brand"
              state="filled"
              prepend-icon="paperPlaneM"
              :disabled="!selectedPServeds.length || isSending"
              data-test="send-contact-to-chat-btn"
              @click="send"
            >
              <span class="sr-only">{{ $t('chat.sendForward') }}</span>
            </wui-btn>
          </div>
        </div>
      </div>
    </div>
  </wui-modal>
</template>

<script setup lang="ts">
import {
  closeDialogKey,
  safeInject,
  useRenderNotifier,
  WuiBtn,
  WuiCheckbox,
  WuiChip,
  WuiIcon,
  WuiInput,
  WuiModal,
  WuiTabs,
  WuiTabsItem,
} from '@wui/common-library'
import type { PServed } from '@wui/im'
import debounce from 'lodash.debounce'
import { storeToRefs } from 'pinia'
import { computed, defineAsyncComponent, nextTick, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'


import { useFilesUpload } from '@/features/chat-files-upload'

import { ALL_CHATS_FOLDER_ID, filterRoomsByFolder, useChatFolders } from '@/entities/chat-folder'
import { useStoreContacts } from '@/entities/contact'
import { ContactAvatar } from '@/entities/contact-avatar'
import { useStoreRooms } from '@/entities/room'
import { useRoom } from '@/entities/use-room'

import { VirtualScroller } from '@/shared/components/virtual-scroller'
import { useCurrentUser } from '@/shared/composables/current-user'
import { useLocalization } from '@/shared/composables/localization'
import { useEmoji } from '@/shared/composables/use-emoji'
import type { Contact } from '@/shared/types/api'
import type { RoomListItem } from '@/shared/types/app'
import type { Room } from '@/shared/types/store'
import { convertUploadingFiles } from '@/shared/utils/convert-uploading-files'
import { createPrivateRoom } from '@/shared/utils/im'
import { contactToVCardFile } from '@/shared/utils/vcard'

const EmojiPicker = defineAsyncComponent(async () => ((await import('@/features/emoji-picker')).EmojiPicker))

const FOLDER_TAB_NAME_MAX_LENGTH = 18
/** Row 52px + 2px gap between items */
const SHARE_LIST_ITEM_HEIGHT = 54
const SCROLL_LOAD_THRESHOLD_PX = 80

const ShareTab = {
  PERSONAL: 'personal',
  GROUPS: 'groups',
} as const

type ShareTabValue = typeof ShareTab[keyof typeof ShareTab]

const props = defineProps<{
  contact?: Contact
  shareText?: string
}>()

const closeModal = safeInject(closeDialogKey)
const { tc, t } = useLocalization()
const { notifyError, notifySuccess } = useRenderNotifier()
const { currentUser } = useCurrentUser()
const route = useRoute()
const roomApi = useRoom()
const storeRooms = useStoreRooms()
const storeContacts = useStoreContacts()
const { roomsWithOtherContactsListItems } = storeToRefs(storeRooms)
const { folders } = useChatFolders()
const { showEmojiList, toggleEmojiList, closeEmojiList } = useEmoji()

const search = ref('')
const selectedFolderId = ref(ALL_CHATS_FOLDER_ID)
const selectedShareTab = ref<ShareTabValue>(ShareTab.PERSONAL)
const selectedPServeds = ref<PServed[]>([])
const messageText = ref('')
const isSending = ref(false)
const commentInputRef = ref<HTMLTextAreaElement | null>(null)

const isInviteShareMode = computed(() => !!props.shareText?.trim() && !props.contact)

const formatFolderTabName = (name: string) => (
  name.length > FOLDER_TAB_NAME_MAX_LENGTH
    ? `${name.slice(0, FOLDER_TAB_NAME_MAX_LENGTH)}…`
    : name
)

const modalTitle = computed(() => {
  if (!selectedPServeds.value.length) {
    return t('chat.whereToForward')
  }

  return tc('chat.selectedChatsCount', selectedPServeds.value.length, {
    n: selectedPServeds.value.length,
  })
})

const selectableRooms = computed(() =>
  roomsWithOtherContactsListItems.value.filter((room) => !!room.pServedUser),
)

const findListItemByPServed = (pServed: PServed) => (
  selectableRooms.value.find((room) => room.pServedUser === pServed)
)

const resolveRoomContact = (room: RoomListItem): Contact | undefined => {
  if (room.isGroup || !room.pServedUser || isOwnRoom(room)) {
    return undefined
  }

  return storeContacts.findByPServed(room.pServedUser)
    ?? room.subscribers?.get(room.pServedUser)
}

const isOwnRoom = (room: RoomListItem) => room.pServedUser === currentUser.value?.imLogin

const isFavoriteOrPinned = (room: RoomListItem) => {
  if (room.isPinned) {
    return true
  }

  if (room.isGroup) {
    return false
  }

  return !!resolveRoomContact(room)?.isFavorite
}

const folderRooms = computed(() => filterRoomsByFolder(
  selectableRooms.value,
  selectedFolderId.value,
  folders.value,
))

const tabRooms = computed(() => {
  if (!isInviteShareMode.value) {
    return folderRooms.value
  }

  return selectableRooms.value.filter((room) => (
    selectedShareTab.value === ShareTab.GROUPS
      ? !!room.isGroup
      : !room.isGroup
  ))
})

const matchingRooms = computed(() => {
  const query = search.value.trim().toLowerCase()
  if (!query) {
    return tabRooms.value
  }

  return tabRooms.value.filter((room) => room.name?.toLowerCase().includes(query))
})

const pinnedRooms = computed(() => matchingRooms.value.filter((room) => isFavoriteOrPinned(room)))
const unpinnedRooms = computed(() => matchingRooms.value.filter((room) => !isFavoriteOrPinned(room)))

const listScrollKey = computed(() => (
  isInviteShareMode.value
    ? selectedShareTab.value
    : selectedFolderId.value
))

const personalSelectedCount = computed(() =>
  selectedPServeds.value.filter((pServed) => {
    const room = findListItemByPServed(pServed)
    if (room) {
      return !room.isGroup
    }

    return !!storeContacts.findByPServed(pServed)
  }).length,
)

const groupSelectedCount = computed(() =>
  selectedPServeds.value.filter((pServed) => {
    const room = findListItemByPServed(pServed)
    return !!room?.isGroup
  }).length,
)

type SelectedChipItem = {
  pServedUser: PServed
  name: string
  room: RoomListItem | undefined
  contact: Contact | undefined
  isGroup: boolean
  isOwn: boolean
}

const selectedChipItems = computed((): SelectedChipItem[] =>
  selectedPServeds.value.flatMap((pServed): SelectedChipItem[] => {
    const room = findListItemByPServed(pServed)
    if (room) {
      return [{
        pServedUser: pServed,
        name: room.name,
        room,
        contact: resolveRoomContact(room),
        isGroup: !!room.isGroup,
        isOwn: isOwnRoom(room),
      }]
    }

    const contact = storeContacts.findByPServed(pServed)
    if (!contact) {
      return []
    }

    return [{
      pServedUser: pServed,
      name: contact.name || pServed,
      room: undefined,
      contact,
      isGroup: false,
      isOwn: pServed === currentUser.value?.imLogin,
    }]
  }),
)

const onFolderChange = (value: string | number) => {
  selectedFolderId.value = String(value)
}

const onShareTabChange = (value: string | number) => {
  selectedShareTab.value = value === ShareTab.GROUPS
    ? ShareTab.GROUPS
    : ShareTab.PERSONAL
}

const ensureContactsLoaded = async () => {
  if (!isInviteShareMode.value) {
    return
  }

  if (storeContacts.isLoading) {
    return
  }

  if (!storeContacts.contactStoreIsReady || !storeContacts.allContacts.length) {
    await storeContacts.fetchNext()
  }
}

const searchContactsIfNeeded = debounce(async (query: string) => {
  if (!isInviteShareMode.value) {
    return
  }

  const normalized = query.trim()
  if (!normalized) {
    return
  }

  await storeContacts.fetchContactsByNameOrNumberIfListIsNotLoaded(normalized)
}, 300)

const onListEndReached = () => {
  if (!isInviteShareMode.value || selectedShareTab.value !== ShareTab.PERSONAL) {
    return
  }

  if (storeContacts.isLoading) {
    return
  }

  void storeContacts.fetchNext()
}

const onListScroll = (event: Event) => {
  const target = event.target as HTMLElement
  const remaining = target.scrollHeight - target.scrollTop - target.clientHeight
  if (remaining > SCROLL_LOAD_THRESHOLD_PX) {
    return
  }

  onListEndReached()
}

const toggleRoom = (pServedUser: PServed) => {
  if (selectedPServeds.value.includes(pServedUser)) {
    selectedPServeds.value = selectedPServeds.value.filter((item) => item !== pServedUser)
    return
  }

  selectedPServeds.value = [...selectedPServeds.value, pServedUser]
}

const appendEmoji = async (emoji: string) => {
  const input = commentInputRef.value
  if (!input) {
    messageText.value += emoji
    return
  }

  const start = input.selectionStart ?? messageText.value.length
  const end = input.selectionEnd ?? messageText.value.length
  messageText.value = `${messageText.value.slice(0, start)}${emoji}${messageText.value.slice(end)}`
  await nextTick()
  const caret = start + emoji.length
  input.focus()
  input.setSelectionRange(caret, caret)
}

const fileListFromFile = (file: File): FileList => {
  const dt = new DataTransfer()
  dt.items.add(file)
  return dt.files
}

const closeDialog = () => {
  closeModal(undefined)
}

const resolveRoom = (pServed: PServed) => {
  const room = storeRooms.findByPServed(pServed)
  if (room) {
    return room
  }

  const contact = storeContacts.findByPServed(pServed)
  if (!contact) {
    return undefined
  }

  return createPrivateRoom(pServed, contact)
}

type ShareSendResult = {
  successCount: number
  uploadFailed?: boolean
}

const sendCommentIfNeeded = async (room: Room) => {
  const comment = messageText.value.trim()
  if (!comment) {
    return
  }

  try {
    await roomApi.send(comment, room)
  } catch (error) {
    notifyError(error instanceof Error ? error.message : t('common.unknownError'))
  }
}

const sendShareText = async (): Promise<number> => {
  const shareText = props.shareText?.trim() ?? ''
  if (!shareText) {
    return 0
  }

  let successCount = 0

  for (const pServed of selectedPServeds.value) {
    const room = resolveRoom(pServed)
    if (!room) {
      continue
    }

    try {
      await roomApi.send(shareText, room)
      successCount += 1
      await sendCommentIfNeeded(room)
    } catch (error) {
      notifyError(error instanceof Error ? error.message : t('common.unknownError'))
    }
  }

  return successCount
}

const sendContactCard = async (): Promise<ShareSendResult> => {
  if (!props.contact) {
    return { successCount: 0 }
  }

  const vcardFile = contactToVCardFile(props.contact)
  const chatFiles = convertUploadingFiles(fileListFromFile(vcardFile), 'share-contact')
  await useFilesUpload().uploadFiles(chatFiles)
  const loadedFiles = chatFiles.filter((file) => file.isLoaded.value && !file.isError.value)
  if (!loadedFiles.length) {
    notifyError(t('mainAside.contactShareError'))
    return { successCount: 0, uploadFailed: true }
  }

  let successCount = 0

  for (const pServed of selectedPServeds.value) {
    const room = resolveRoom(pServed)
    if (!room) {
      continue
    }

    try {
      await roomApi.send('', room, loadedFiles)
      successCount += 1
      await sendCommentIfNeeded(room)
    } catch (error) {
      notifyError(error instanceof Error ? error.message : t('common.unknownError'))
    }
  }

  return { successCount }
}

const send = async () => {
  if (!selectedPServeds.value.length || isSending.value) {
    return
  }

  if (!props.contact && !props.shareText?.trim()) {
    return
  }

  isSending.value = true
  closeEmojiList()

  try {
    let successCount = 0

    if (props.contact) {
      const result = await sendContactCard()
      if (result.uploadFailed) {
        return
      }
      successCount = result.successCount
    } else {
      successCount = await sendShareText()
    }

    if (successCount <= 0) {
      return
    }

    notifySuccess(tc('chat.forwardedToChatsCount', successCount, { n: successCount }))
    closeDialog()
  } catch (error) {
    const message = error instanceof Error ? error.message : t('common.unknownError')
    notifyError(message)
  } finally {
    isSending.value = false
  }
}

watch(() => route.path, () => {
  closeDialog()
})

watch(folders, (nextFolders) => {
  if (
    selectedFolderId.value !== ALL_CHATS_FOLDER_ID
    && !nextFolders.some((folder) => folder.id === selectedFolderId.value)
  ) {
    selectedFolderId.value = ALL_CHATS_FOLDER_ID
  }
})

watch(search, (value) => {
  void searchContactsIfNeeded(value)
})

onMounted(() => {
  void ensureContactsLoaded()
})
</script>
